import * as faceapi from 'face-api.js';

const MODEL_URL = '/models';
const STORAGE_KEY = 'faceRecognition_ownerDescriptor';
const MATCH_THRESHOLD = 0.55;

let modelsLoaded = false;
let loadingPromise = null;

/**
 * Load face-api.js models (TinyFaceDetector, FaceLandmark68, FaceRecognition).
 * Safe to call multiple times — will only load once.
 */
export async function loadModels() {
  if (modelsLoaded) return true;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    try {
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      modelsLoaded = true;
      console.log('[FaceRec] Models loaded successfully');
      return true;
    } catch (err) {
      console.error('[FaceRec] Failed to load models:', err);
      loadingPromise = null;
      return false;
    }
  })();

  return loadingPromise;
}

/**
 * Detect faces in a video element and return recognition status.
 * @param {HTMLVideoElement} video
 * @returns {{ status: string, faceCount: number, distance: number|null }}
 *   status: 'no-one' | 'detected' | 'recognized' | 'stranger'
 */
export async function recognizeFromVideo(video) {
  if (!modelsLoaded) {
    const ok = await loadModels();
    if (!ok) return { status: 'no-one', faceCount: 0, distance: null };
  }

  const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.4 });

  const detections = await faceapi
    .detectAllFaces(video, options)
    .withFaceLandmarks()
    .withFaceDescriptors();

  if (!detections || detections.length === 0) {
    return { status: 'no-one', faceCount: 0, distance: null };
  }

  const ownerDescriptor = getOwnerDescriptor();
  if (!ownerDescriptor) {
    return { status: 'detected', faceCount: detections.length, distance: null };
  }

  // Find the closest face to the owner
  let bestDistance = Infinity;
  for (const det of detections) {
    const dist = faceapi.euclideanDistance(det.descriptor, ownerDescriptor);
    if (dist < bestDistance) bestDistance = dist;
  }

  if (bestDistance <= MATCH_THRESHOLD) {
    return { status: 'recognized', faceCount: detections.length, distance: bestDistance };
  } else {
    return { status: 'stranger', faceCount: detections.length, distance: bestDistance };
  }
}

/**
 * Enroll the owner's face by capturing multiple samples from the webcam.
 * @param {HTMLVideoElement} video - live webcam video element
 * @param {number} samples - number of frames to capture (default 5)
 * @param {number} delayMs - delay between samples in ms (default 600)
 * @param {function} onProgress - callback(current, total) for progress updates
 * @returns {boolean} true if enrollment succeeded
 */
export async function enrollFace(video, samples = 5, delayMs = 600, onProgress = null) {
  if (!modelsLoaded) {
    const ok = await loadModels();
    if (!ok) return false;
  }

  const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.4 });
  const descriptors = [];

  for (let i = 0; i < samples; i++) {
    if (onProgress) onProgress(i + 1, samples);

    const detection = await faceapi
      .detectSingleFace(video, options)
      .withFaceLandmarks()
      .withFaceDescriptor();

    if (detection) {
      descriptors.push(Array.from(detection.descriptor));
    }

    if (i < samples - 1) {
      await new Promise(r => setTimeout(r, delayMs));
    }
  }

  if (descriptors.length < 2) {
    console.warn('[FaceRec] Enrollment failed: not enough face samples captured', descriptors.length);
    return false;
  }

  // Average all descriptors into a single 128-dim vector
  const avg = new Float32Array(128);
  for (const desc of descriptors) {
    for (let j = 0; j < 128; j++) {
      avg[j] += desc[j];
    }
  }
  for (let j = 0; j < 128; j++) {
    avg[j] /= descriptors.length;
  }

  // Store in localStorage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(avg)));
  console.log(`[FaceRec] Enrolled owner face with ${descriptors.length}/${samples} samples`);
  return true;
}

/**
 * Get the stored owner face descriptor from localStorage.
 * @returns {Float32Array|null}
 */
export function getOwnerDescriptor() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return new Float32Array(JSON.parse(stored));
  } catch {
    return null;
  }
}

/**
 * Check if an owner face has been enrolled.
 * @returns {boolean}
 */
export function hasEnrolledFace() {
  return !!localStorage.getItem(STORAGE_KEY);
}

/**
 * Clear the enrolled owner face.
 */
export function clearEnrolledFace() {
  localStorage.removeItem(STORAGE_KEY);
  console.log('[FaceRec] Owner face cleared');
}
